/**
 * API Configuration Utility
 * 
 * Provides consistent base URL determination for API endpoints
 * across the application to prevent inconsistent API calls.
 */

/**
 * Tenant configuration interface
 */
export interface TenantConfig {
  tenant_id: string;
  apiUrl: string;
  apiPath: string;
  fullApiUrl: string;
  webSocketUrl: string;
}

/**
 * Get the tenant ID from the current hostname or environment
 */
export const getTenantIdFromHostname = (): string => {
  const hostname = window.location.hostname;
  
  // Handle development environment
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_DEFAULT_TENANT_ID || 'brain';
  }

  // Handle platform admin (special case - not a tenant)
  if (hostname === 'admin.astrapeak.ai') {
    return 'platform-admin';
  }

  // Extract tenant from hostname (e.g., prospance-brain.astrapeak.ai -> prospance)
  const match = hostname.match(/^([^-]+)-brain\./);
  if (match && match[1]) {
    return match[1];
  }
  
  // Default fallback
  return import.meta.env.VITE_DEFAULT_TENANT_ID || 'brain';
};

/**
 * Get the base URL for API requests based on the current environment
 * and hostname with proper tenant context.
 */
export const getBaseUrl = (): string => {
  const hostname = window.location.hostname;
  
  // SPECIAL CASE: Platform Admin (admin.astrapeak.ai)
  // Platform Admin uses its own CloudFront with ALB origin for API requests
  if (hostname === 'admin.astrapeak.ai') {
    return `https://${hostname}`;  // Use same domain - CloudFront routes /api/* to ALB
  }
  
  // For tenant-specific domains, use the current hostname
  if (hostname.includes('-brain.astrapeak.ai')) {
    return `https://${hostname}`;
  }
  
  // For localhost development, use environment variable or default to dev
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_BRAIN_API_URL || 'https://dev-brain.astrapeak.ai';
  }
  
  // For non-tenant domains, use the tenant-specific domain
  const tenant_id = getTenantIdFromHostname();
  return `https://${tenant_id}-brain.astrapeak.ai`;
};

/**
 * Get the API path for the current tenant
 * Updated: Complete V1 elimination - backend migrated to /api/* structure
 */
export const getApiPath = (): string => {
  return '/api';
};

/**
 * Get the full API URL (base + path) for the current tenant
 */
export const getFullApiUrl = (): string => {
  return `${getBaseUrl()}${getApiPath()}`;
};

/**
 * Get the WebSocket URL for the current tenant
 */
export const getWebSocketUrl = (): string => {
  const baseUrl = getBaseUrl();
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const hostname = new URL(baseUrl).hostname;
  
  return `${protocol}//${hostname}/api/ws`;
};

/**
 * Get the tenant configuration
 */
export const getTenantConfig = (): TenantConfig => {
  const tenant_id = getTenantIdFromHostname();
  
  return {
    tenant_id,
    apiUrl: getBaseUrl(),
    apiPath: getApiPath(),
    fullApiUrl: getFullApiUrl(),
    webSocketUrl: getWebSocketUrl()
  };
};

/**
 * Create a complete API URL by combining the base URL with the endpoint path
 */
export const createApiUrl = (endpoint: string): string => {
  const baseUrl = getBaseUrl();
  
  // Ensure endpoint starts with a slash if not already
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  return `${baseUrl}${normalizedEndpoint}`;
};

// Export a function that always returns fresh configuration
// This prevents stale values from module-load-time initialization
export const tenantConfig = getTenantConfig;

// Export default for convenience - always returns fresh config
export default getTenantConfig;
